import React, { createContext, ReactNode, useContext } from 'react';
import { isReady } from 'shared/src/utils/zod/trpc-zod-utils';
import { TRPCQueryResult } from '@/utils/trpc-client';
import { MediaPlanApiListResponse } from 'shared/src/api/media-plan-api-types';
import {
  ExecutionLineItemTrpcResponse,
  TrpcLineItemGraphMetrics,
  TrpcLineItemMetrics,
  TrpcMediaPlansMetrics
} from 'shared/src/trpc-types/line-item-trpc-types';
import { MediaPlan } from 'shared/src/domain/media-buy/media-buy-types';

type ContextValue = {
  lineItem: ExecutionLineItemTrpcResponse;
  graphMetrics: TrpcLineItemGraphMetrics;
  mediaPlans: MediaPlan[];
  mediaPlansMetrics: TrpcMediaPlansMetrics;
  lineItemMetrics: TrpcLineItemMetrics;
};

const LineItemContext = createContext<ContextValue | null>(null);

type Props = {
  lineItem: ExecutionLineItemTrpcResponse;
  graphMetrics: TRPCQueryResult<TrpcLineItemGraphMetrics>;
  mediaPlans: TRPCQueryResult<MediaPlanApiListResponse>;
  mediaPlansMetrics: TRPCQueryResult<TrpcMediaPlansMetrics>;
  lineItemMetrics: TRPCQueryResult<TrpcLineItemMetrics>;
  children: ReactNode;
};

// handle waiting for data to load before rendering
export function LineItemContextProvider({
  lineItem,
  graphMetrics,
  mediaPlans,
  mediaPlansMetrics,
  lineItemMetrics,
  children
}: Props) {
  if (
    graphMetrics.isError ||
    mediaPlans.isError ||
    mediaPlansMetrics.isError ||
    lineItemMetrics.isError
  ) {
    return <div>Error loading line item data.</div>;
  }

  if (
    !isReady(graphMetrics) ||
    !isReady(mediaPlans) ||
    !isReady(mediaPlansMetrics) ||
    !isReady(lineItemMetrics)
  ) {
    return <div>Loading...</div>;
  }

  return (
    <LineItemContext
      value={{
        lineItem,
        graphMetrics: graphMetrics.data,
        mediaPlans: mediaPlans.data.data,
        mediaPlansMetrics: mediaPlansMetrics.data,
        lineItemMetrics: lineItemMetrics.data
      }}>
      {children}
    </LineItemContext>
  );
}

export function useLineItemContext() {
  const value = useContext(LineItemContext);
  if (!value) {
    throw new Error('useLineItemContext must be used within a LineItemContextProvider');
  }
  return value;
}
